import React, { useState, useEffect } from 'react';
import { 
  Grid, Paper, Typography, Box, FormControl, Select, MenuItem, InputLabel,
  Checkbox, ListItemText, OutlinedInput, ListSubheader, Alert,
  Dialog, DialogTitle, DialogContent, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Bar, Doughnut } from 'react-chartjs-2';
import LoadingState from './loadingstate';
import Papa from 'papaparse';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,  
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js elements
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement, 
  Title,
  Tooltip,
  Legend
);

// Districts with mangrove data (from your CSV)
const divisionGroupings = {
  'Kuching': ['Kuching', 'Lundu'],
  'Samarahan': ['Samarahan', 'Asajaya', 'Simunjan'],
  'Betong': ['Betong', 'Saratok'],
  'Sarikei': ['Sarikei'],
  'Mukah': ['Mukah', 'Dalat', 'Matu', 'Daro'],
  'Limbang': ['Limbang', 'Lawas'],
  'Miri': ['Miri'],
  'Meradong': ['Meradong'],
  'Sri Aman': ['Sri Aman']
};

const availableYears = [2010, 2015, 2020];

const MangroveCoverAnalysis = () => {
  const [selectedDistricts, setSelectedDistricts] = useState(['Kuching', 'Sarikei']);
  const [selectedYears, setSelectedYears] = useState([2010, 2015, 2020]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedChart, setExpandedChart] = useState(null);
  
  const allDistricts = Object.values(divisionGroupings).flat();
  
  // Fetch JSON data once when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        // Call the NEW mangrove data endpoint
        const response = await fetch(`${API_BASE_URL}/analysis/mangrove-data`);
        
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || 'Failed to fetch data');
        }
        
        const jsonData = await response.json();
        
        // No more Papa.parse! The data is already JSON.
        // We just need to make sure null values (for districts with no mangroves) are 0
        const cleanedData = jsonData.map(row => ({
          ...row,
          mangrove_2010: row.mangrove_2010 || 0,
          mangrove_2015: row.mangrove_2015 || 0,
          mangrove_2020: row.mangrove_2020 || 0,
        }));
        
        setAllData(cleanedData);

      } catch (err) {
        console.error('Error fetching mangrove data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Filter data based on selected districts
  const filteredData = allData.filter(row => 
    selectedDistricts.includes(row.district) && 
    (row.mangrove_2010 || row.mangrove_2015 || row.mangrove_2020)
  );
  
  const handleDistrictSelectChange = (event) => {
    const { target: { value } } = event;
    
    if (value.includes('all')) {
      setSelectedDistricts(allDistricts);
      return;
    }
    
    if (value.includes('none')) {
      setSelectedDistricts([]);
      return;
    }
    
    setSelectedDistricts(typeof value === 'string' ? value.split(',') : value);
  };

  const handleYearSelectChange = (event) => {
    const { target: { value } } = event;
    
    if (value.includes('all')) {
      setSelectedYears(availableYears);
      return;
    }
    
    setSelectedYears(typeof value === 'string' ? value.split(',').map(Number) : value);
  };
  
  const handleExpandChart = (chartType) => setExpandedChart(chartType);
  const handleCloseDialog = () => setExpandedChart(null);
  
  // Color scheme for years - using green/teal tones for mangrove
  const yearColors = {
    2010: { bg: 'rgba(156, 39, 176, 0.7)', border: 'rgba(156, 39, 176, 1)' },    // Purple
    2015: { bg: 'rgba(255, 152, 0, 0.7)', border: 'rgba(255, 152, 0, 1)' },      // Orange
    2020: { bg: 'rgba(0, 188, 212, 0.7)', border: 'rgba(0, 188, 212, 1)' }       // Cyan
  };
  
  const mangroveTrendData = filteredData.length > 0 && selectedYears.length > 0 ? {
    labels: filteredData.map(d => d.district),
    datasets: selectedYears.map(year => ({
      label: year.toString(),
      data: filteredData.map(d => d[`mangrove_${year}`] || 0),
      backgroundColor: yearColors[year]?.bg || 'rgba(100, 100, 100, 0.7)',
      borderColor: yearColors[year]?.border || 'rgba(100, 100, 100, 1)',
      borderWidth: 2
    }))
  } : null;
  
  const oldestYear = selectedYears.length > 0 ? Math.min(...selectedYears) : 2010;
  const newestYear = selectedYears.length > 0 ? Math.max(...selectedYears) : 2020;
  
  const changeRateData = filteredData.length > 0 ? {
    labels: filteredData.map(d => d.district),
    datasets: [{
      label: `Mangrove Cover Change ${oldestYear}-${newestYear} (%)`,
      data: filteredData.map(d => {
        const startVal = d[`mangrove_${oldestYear}`] || 0;
        const endVal = d[`mangrove_${newestYear}`] || 0;
        if (startVal === 0) return 0;
        return parseFloat(((endVal - startVal) / startVal * 100).toFixed(2));
      }),
      backgroundColor: filteredData.map(d => {
        const startVal = d[`mangrove_${oldestYear}`] || 0;
        const endVal = d[`mangrove_${newestYear}`] || 0;
        const change = startVal > 0 ? (endVal - startVal) / startVal * 100 : 0;
        return change < 0 ? 'rgba(220, 38, 38, 0.7)' : 'rgba(34, 197, 94, 0.7)';
      }),
      borderColor: filteredData.map(d => {
        const startVal = d[`mangrove_${oldestYear}`] || 0;
        const endVal = d[`mangrove_${newestYear}`] || 0;
        const change = startVal > 0 ? (endVal - startVal) / startVal * 100 : 0;
        return change < 0 ? 'rgba(220, 38, 38, 1)' : 'rgba(34, 197, 94, 1)';
      }),
      borderWidth: 2
    }] 
  } : null;
  
  const latestYear = selectedYears.length > 0 ? Math.max(...selectedYears) : 2020;
  const totalMangroveData = filteredData.length > 0 ? {
    labels: filteredData.map(d => d.district),
    datasets: [{
      data: filteredData.map(d => d[`mangrove_${latestYear}`] || 0),
      backgroundColor: [
        'rgba(156, 39, 176, 0.8)',    // Purple
        'rgba(233, 30, 99, 0.8)',     // Pink
        'rgba(33, 150, 243, 0.8)',    // Blue
        'rgba(0, 188, 212, 0.8)',     // Cyan
        'rgba(0, 150, 136, 0.8)',     // Teal
        'rgba(76, 175, 80, 0.8)',     // Green
        'rgba(139, 195, 74, 0.8)',    // Light Green
        'rgba(255, 235, 59, 0.8)',    // Yellow
        'rgba(255, 152, 0, 0.8)',     // Orange
        'rgba(244, 67, 54, 0.8)',     // Red
        'rgba(121, 85, 72, 0.8)',     // Brown
        'rgba(96, 125, 139, 0.8)',    // Blue Grey
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }] 
  } : null;

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(2)} km²`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Mangrove Cover (km²)' }
      }
    }
  };
  
  const changeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return `Change: ${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
          }
        }
      }
    },
    scales: {
      y: {
        title: { display: true, text: 'Percentage Change (%)' },
        grid: {
          color: (context) => context.tick.value === 0 ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)'
        }
      }
    }
  };

  return (
    <Box>
      <Grid container spacing={2} alignItems="center" mb={3}>
        <Grid item xs={12} md={3}>
          <Typography variant="h6">Mangrove Cover Analysis</Typography>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl size="small" fullWidth>
            <InputLabel id="district-multi-select-label">Select Districts</InputLabel>
            <Select
              labelId="district-multi-select-label"
              multiple
              value={selectedDistricts}
              onChange={handleDistrictSelectChange}
              input={<OutlinedInput label="Select Districts" />}
              renderValue={(selected) => `${selected.length} district${selected.length !== 1 ? 's' : ''}`}
              MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
            >
              <MenuItem value="all">
                <Checkbox 
                  checked={allDistricts.length > 0 && selectedDistricts.length === allDistricts.length}
                  indeterminate={selectedDistricts.length > 0 && selectedDistricts.length < allDistricts.length}
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              <MenuItem value="none">
                <Checkbox checked={selectedDistricts.length === 0} />
                <ListItemText primary="Deselect All" />
              </MenuItem>

              {Object.entries(divisionGroupings).flatMap(([division, districts]) => [
                <ListSubheader key={`${division}-header`}>{division} Division</ListSubheader>,
                ...districts.map((district) => (
                  <MenuItem key={district} value={district} sx={{ pl: 4 }}>
                    <Checkbox checked={selectedDistricts.includes(district)} />
                    <ListItemText primary={district} />
                  </MenuItem>
                ))
              ])}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl size="small" fullWidth>
            <InputLabel id="year-multi-select-label">Display Years</InputLabel>
            <Select
              labelId="year-multi-select-label"
              multiple
              value={selectedYears}
              onChange={handleYearSelectChange}
              input={<OutlinedInput label="Display Years" />}
              renderValue={(selected) => selected.sort().join(', ')}
            >
              <MenuItem value="all">
                <Checkbox checked={selectedYears.length === availableYears.length} />
                <ListItemText primary="All Years" />
              </MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  <Checkbox checked={selectedYears.includes(year)} />
                  <ListItemText primary={year} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      
      <LoadingState loading={loading} error={error}>
        {filteredData.length === 0 ? (
          <Alert severity="info">Please select at least one district to see mangrove cover analysis.</Alert>
        ) : selectedYears.length === 0 ? (
          <Alert severity="warning">Please select at least one year to display in the trend chart.</Alert>
        ) : (
          <Box>
            {/* Top Row: Mangrove Cover Trend (60%) and Distribution (40%) */}
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 3,
                mb: 3,
                justifyContent: 'space-between'
              }}
            >
              {/* Mangrove Cover Trend - 60% width */}
              <Paper 
                sx={{ 
                  p: { xs: 2, md: 3 },
                  cursor: 'pointer',
                  flex: { xs: 'none', lg: '1 1 60%' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onClick={() => handleExpandChart('trend')}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Mangrove Cover Trend ({selectedYears.sort().join(', ')})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    🔍 Click to enlarge
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: { xs: 350, md: 500 }, flex: 1 }}>
                  <Bar data={mangroveTrendData} options={barChartOptions} />
                </Box>
                <Alert severity="info" sx={{ mt: 2, '& .MuiAlert-message': { width: '100%', textAlign: 'center' } }}>
                  <Typography variant="caption">
                    Compares mangrove cover across selected time periods. Hover over bars to see exact values in km².
                  </Typography>
                </Alert>
              </Paper>

              {/* District Distribution - 40% width */}
              <Paper 
                sx={{ 
                  p: { xs: 2, md: 3 },
                  cursor: 'pointer',
                  flex: { xs: 'none', lg: '1 1 40%' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onClick={() => handleExpandChart('distribution')}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Mangrove Distribution by District ({latestYear})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    🔍 Click to enlarge
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: { xs: 350, md: 500 }, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Doughnut 
                    data={totalMangroveData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { 
                          position: 'right',
                          labels: {
                            boxWidth: 15,
                            padding: 10,
                            font: { size: 11 }
                          }
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const value = context.parsed;
                              const total = context.dataset.data.reduce((a, b) => a + b, 0);
                              const percentage = ((value / total) * 100).toFixed(2);
                              return `${context.label}: ${percentage}% (${value.toFixed(2)} km²)`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </Box>
                <Alert severity="info" sx={{ mt: 2, '& .MuiAlert-message': { width: '100%', textAlign: 'center' } }}>
                  <Typography variant="caption">
                    Shows the proportion of mangrove cover across selected districts in {latestYear}.
                  </Typography>
                </Alert>
              </Paper>
            </Box>

            {/* Bottom Row: Change Rate - Full width */}
            <Paper 
              sx={{ 
                p: { xs: 2, md: 3 },
                cursor: 'pointer'
              }}
              onClick={() => handleExpandChart('change')}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Mangrove Cover Change Rate ({oldestYear}-{newestYear})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  🔍 Click to enlarge
                </Typography>
              </Box>
              <Box sx={{ width: '100%', height: { xs: 350, md: 400 } }}>
                <Bar data={changeRateData} options={changeChartOptions} />
              </Box>
              <Alert severity="info" sx={{ mt: 2, '& .MuiAlert-message': { width: '100%', textAlign: 'center' } }}>
                <Typography variant="caption">
                  <strong>Green bars</strong> indicate mangrove growth, <strong>red bars</strong> indicate mangrove loss.
                  <br />Percentage change is calculated from {oldestYear} baseline to {newestYear}.
                </Typography>
              </Alert>
            </Paper>
          </Box>
        )}
      </LoadingState>

      {/* Dialog for expanded charts */}
      <Dialog open={!!expandedChart} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {expandedChart === 'trend' && `Mangrove Cover Trend (${selectedYears.sort().join(', ')})`}
          {expandedChart === 'distribution' && `Mangrove Distribution by District (${latestYear})`}
          {expandedChart === 'change' && `Mangrove Cover Change Rate (${oldestYear}-${newestYear})`}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box height={{ xs: '60vh', md: 500 }}>
            {expandedChart === 'trend' && mangroveTrendData && (
              <Bar data={mangroveTrendData} options={barChartOptions} />
            )}
            {expandedChart === 'distribution' && totalMangroveData && (
              <Doughnut 
                data={totalMangroveData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'right' },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const value = context.parsed;
                          const total = context.dataset.data.reduce((a, b) => a + b, 0);
                          const percentage = ((value / total) * 100).toFixed(2);
                          return `${context.label}: ${percentage}% (${value.toFixed(2)} km²)`;
                        }
                      }
                    }
                  }
                }}
              />
            )}
            {expandedChart === 'change' && changeRateData && (
              <Bar data={changeRateData} options={changeChartOptions} />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MangroveCoverAnalysis;